// IP Scanner Service - Scans local network for miners and extracts configuration
// Based on Android Kotlin implementation

export interface MinerDevice {
  ip: string;
  workerName: string;
  status: 'online' | 'offline' | 'error';
  lastSeen: number;
}

export interface ScanProgress {
  currentIp: string;
  scannedCount: number;
  totalIps: number;
  foundMiners: number;
}

export interface ScanResult {
  miners: MinerDevice[];
  totalScanned: number;
  successCount: number;
  authFailCount: number;
  errorCount: number;
  scanDuration: number;
}

class IPScannerService {
  private isScanning = false;
  private abortController: AbortController | null = null;

  // Configuration matching Android implementation
  private readonly config = {
    baseNetwork: '192.168.1.',
    username: 'root',
    password: 'root',
    timeout: 3000,
    readTimeout: 5000,
    maxConcurrency: 15,
    ipRange: { start: 1, end: 254 }
  };

  /**
   * Scan network for miners with progress callback
   */
  async scanNetworkWithProgress(
    onProgress: (progress: ScanProgress) => void
  ): Promise<ScanResult> {
    if (this.isScanning) {
      throw new Error('Scan already in progress');
    }

    this.isScanning = true;
    this.abortController = new AbortController();
    
    const startTime = Date.now();
    const result: MinerDevice[] = [];
    let scannedCount = 0;
    let successCount = 0;
    let authFailCount = 0;
    let errorCount = 0;

    console.log('🔍 Starting IP scan for miners through backend...');

    try {
      // Create semaphore for concurrency control
      const semaphore = new Semaphore(this.config.maxConcurrency);
      
      // Generate all IP addresses to scan
      const ips = Array.from(
        { length: this.config.ipRange.end - this.config.ipRange.start + 1 },
        (_, i) => `${this.config.baseNetwork}${this.config.ipRange.start + i}`
      );

      // Create scan promises for all IPs
      const scanPromises = ips.map(ip => 
        this.scanSingleIP(ip, semaphore, (ip) => {
          scannedCount++;
          onProgress({
            currentIp: ip,
            scannedCount,
            totalIps: ips.length,
            foundMiners: result.length
          });
        })
      );

      // Execute all scans concurrently
      const scanResults = await Promise.allSettled(scanPromises);

      // Process results
      scanResults.forEach((scanResult, index) => {
        if (scanResult.status === 'fulfilled') {
          const { miner, status } = scanResult.value;
          
          switch (status) {
            case 'success':
              successCount++;
              if (miner) {
                result.push(miner);
              }
              break;
            case 'auth_fail':
              authFailCount++;
              break;
            case 'error':
              errorCount++;
              break;
          }
        } else {
          errorCount++;
          console.error(`❌ Scan failed for ${ips[index]}:`, scanResult.reason);
        }
      });

      const scanDuration = Date.now() - startTime;
      
      console.log(`✅ Scan completed in ${scanDuration}ms`);
      console.log(`📊 Results: ${successCount} success, ${authFailCount} auth fails, ${errorCount} errors`);
      console.log(`🎯 Found ${result.length} miners`);

      return {
        miners: result,
        totalScanned: ips.length,
        successCount,
        authFailCount,
        errorCount,
        scanDuration
      };

    } catch (error) {
      console.error('❌ Critical error during IP scanning:', error);
      throw error;
    } finally {
      this.isScanning = false;
      this.abortController = null;
    }
  }

  /**
   * Scan a single IP address for miner configuration
   */
  private async scanSingleIP(
    ip: string,
    semaphore: Semaphore,
    onProgress: (ip: string) => void
  ): Promise<{ miner: MinerDevice | null; status: 'success' | 'auth_fail' | 'error' }> {
    return semaphore.acquire(async () => {
      try {
        onProgress(ip);

        const url = `http://${ip}/cgi-bin/get_miner_conf.cgi`;
        
        // Create request with digest authentication
        const response = await this.makeAuthenticatedRequest(url);

        if (response.ok) {
          console.log(`✅ ${ip} responded with 200 OK`);
          
          const body = await response.text();
          if (body && body.trim()) {
            try {
              const config = JSON.parse(body);
              const workerName = this.extractWorkerName(config);
              
              if (workerName && workerName !== 'null') {
                const miner: MinerDevice = {
                  ip,
                  workerName,
                  status: 'online',
                  lastSeen: Date.now()
                };
                
                console.log(`🎯 ${ip} miner found: ${workerName}`);
                return { miner, status: 'success' };
              }
            } catch (jsonError) {
              console.warn(`⚠️ ${ip} JSON parse error:`, jsonError);
            }
          }
          
          return { miner: null, status: 'success' };
        } else if (response.status === 401) {
          console.warn(`🔒 ${ip} responded 401 (Digest auth failed)`);
          return { miner: null, status: 'auth_fail' };
        } else {
          console.warn(`⚠️ ${ip} => HTTP ${response.status}`);
          return { miner: null, status: 'error' };
        }

      } catch (error) {
        // Don't log expected network errors (timeouts, connection refused, etc.)
        if (this.isExpectedNetworkError(error)) {
          return { miner: null, status: 'error' };
        }
        
        console.error(`❌ ${ip} unexpected error:`, error);
        return { miner: null, status: 'error' };
      }
    });
  }

  /**
   * Make authenticated HTTP request through backend scanner API
   * The backend handles all the network scanning and authentication
   */
  private async makeAuthenticatedRequest(url: string): Promise<Response> {
    try {
      // Import apiClient here to avoid circular dependencies
      const { apiClient } = await import('./api');
      
      const backendData = await apiClient.scanMinerIP(
        url,
        this.config.username,
        this.config.password
      );
      
      // Create a mock Response object with the data from backend
      return {
        ok: backendData.success,
        status: backendData.status || 200,
        text: async () => backendData.data || '',
        json: async () => {
          try {
            return JSON.parse(backendData.data || '{}');
          } catch {
            return {};
          }
        }
      } as Response;
    } catch (error) {
      // Handle network errors and create a failed response
      return {
        ok: false,
        status: 500,
        text: async () => '',
        json: async () => ({})
      } as Response;
    }
  }

  /**
   * Extract worker name from miner configuration JSON
   */
  private extractWorkerName(config: any): string | null {
    try {
      // Try to extract from pools array (most common structure)
      if (config.pools && Array.isArray(config.pools) && config.pools.length > 0) {
        const firstPool = config.pools[0];
        if (firstPool.user) {
          return firstPool.user;
        }
      }

      // Try alternative structures
      if (config.user) {
        return config.user;
      }

      if (config.worker) {
        return config.worker;
      }

      return null;
    } catch (error) {
      console.warn('Error extracting worker name:', error);
      return null;
    }
  }

  /**
   * Check if error is expected network error (timeout, connection refused, etc.)
   */
  private isExpectedNetworkError(error: any): boolean {
    if (!error) return false;
    
    const errorMessage = error.message?.toLowerCase() || '';
    const errorName = error.name?.toLowerCase() || '';
    
    return (
      errorName === 'aborterror' ||
      errorMessage.includes('timeout') ||
      errorMessage.includes('connection refused') ||
      errorMessage.includes('network error') ||
      errorMessage.includes('fetch') ||
      errorMessage.includes('econnrefused') ||
      errorMessage.includes('enotfound') ||
      errorMessage.includes('etimedout')
    );
  }

  /**
   * Stop current scan
   */
  stopScan(): void {
    if (this.isScanning && this.abortController) {
      console.log('🛑 Stopping IP scan...');
      this.abortController.abort();
      this.isScanning = false;
    }
  }

  /**
   * Check if scan is currently running
   */
  get isScanRunning(): boolean {
    return this.isScanning;
  }
}

/**
 * Simple semaphore implementation for concurrency control
 */
class Semaphore {
  private permits: number;
  private waitQueue: Array<() => void> = [];

  constructor(permits: number) {
    this.permits = permits;
  }

  async acquire<T>(fn: () => Promise<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      const tryAcquire = () => {
        if (this.permits > 0) {
          this.permits--;
          fn()
            .then(resolve)
            .catch(reject)
            .finally(() => {
              this.permits++;
              if (this.waitQueue.length > 0) {
                const next = this.waitQueue.shift()!;
                next();
              }
            });
        } else {
          this.waitQueue.push(tryAcquire);
        }
      };

      tryAcquire();
    });
  }
}

// Export singleton instance
export const ipScannerService = new IPScannerService();
export default ipScannerService;